import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import { IMAGES, OFFICE_EMAIL, OFFICE_LOCATION, OFFICE_PHONE } from "@/data/content";
import { useSEO } from "@/lib/useSEO";

const FIELDS = [
  ["Name", "[Advocate's Full Enrolled Name]"],
  ["Designation", "Advocate"],
  ["Professional Qualification", "[Verified Qualifications]"],
  ["Bar Enrolment", "[Enrolment Number and Date]"],
  ["State Bar Council", "[State Bar Council]"],
  ["Bar Association", "[Bar Association]"],
  ["Courts and Forums", "[Factually Applicable Courts and Forums]"],
  ["Languages", "[Verified Languages]"],
  ["Office Location", OFFICE_LOCATION],
  ["Telephone", OFFICE_PHONE],
  ["Professional Email", OFFICE_EMAIL],
];

export default function Profile() {
  useSEO("Advocate Profile | Smart Legal Solutions", "Verifiable professional particulars of the advocate office at Smart Legal Solutions, Vijayawada, Andhra Pradesh.");

  return (
    <div data-testid="profile-page" className="bg-warm pt-32">
      <div className="mx-auto max-w-7xl px-5 pb-24 lg:px-10">
        <div className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="mono-label-dark">Smart Legal Solutions</p>
              <h1 className="mt-4 font-serif text-4xl font-medium leading-[1.1] tracking-tight text-navy sm:text-5xl lg:text-6xl">
                Advocate Profile
              </h1>
              <div className="gold-hairline mt-8" />
              <p className="mt-8 max-w-md font-serif text-xl italic leading-relaxed text-navy/70">
                &ldquo;The practice is founded on careful document review, procedural clarity, legal research and
                responsible representation.&rdquo;
              </p>
              <p className="mt-6 max-w-md text-sm leading-relaxed text-navy/55">
                Only verifiable professional particulars are published. The office does not display success rates,
                client counts, comparative claims or unverified titles.
              </p>
              <Link to="/contact" data-testid="profile-contact-btn" className="btn-outline-dark mt-10">
                Contact Office <ArrowRight size={13} />
              </Link>
            </Reveal>
            <Reveal delay={0.15} className="mt-14 hidden lg:block">
              <div className="relative">
                <div className="absolute -left-4 -top-4 h-full w-full border border-gold/50" aria-hidden="true" />
                <img
                  src={IMAGES.office}
                  alt="A premium legal office interior with natural light across a working desk"
                  className="relative h-80 w-full object-cover grayscale-[30%]"
                  loading="lazy"
                />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <div className="border border-navy/15 bg-ivory">
                <div className="border-b border-navy/10 px-8 py-6">
                  <p className="mono-label-dark">Professional Particulars</p>
                </div>
                {FIELDS.map(([k, v]) => (
                  <div key={k} data-testid={`profile-field-${k.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="grid gap-1 border-b border-navy/5 px-8 py-5 last:border-0 sm:grid-cols-3 sm:gap-6">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate">{k}</span>
                    <span className="col-span-2 font-serif text-lg font-medium text-navy">{v}</span>
                  </div>
                ))}
              </div>
              <p className="mt-6 font-mono text-[10px] leading-relaxed tracking-[0.06em] text-slate">
                Placeholder particulars are replaced only with verified information supplied by the office. A
                professional portrait is displayed only if supplied and approved by the advocate.
              </p>
            </Reveal>
          </div>
        </div>
      </div>
    </div>
  );
}
